import { PasswordStatus } from "components/PasswordRequirement";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { CountryCode } from "../../utils/countries";
import { Address } from "components/FullAddressInput/FullAddressInput";
import { EMAIL_REGEX } from "services/util";

type Account = {
  firstName?: string;
  lastName?: string;
  email?: string;
  countryCode?: CountryCode;
  password?: string;
  confirmPassword?: string;
  code?: string;
  location?: {
    address: Address;
    timezoneId: string;
    utcOffset: string;
  };
  primaryLanguage?: string;
};

type Errors = {
  [key in keyof Account]?: string;
};

type Touched = {
  [key in keyof Account]?: boolean;
};

function useSignInForm(initialData: Account, mfaMode = false) {
  const { t } = useTranslation();
  const [account, setAccount] = useState<Account>(initialData);
  const [errors, setErrors] = useState<Errors>({});
  const [touched, setTouched] = useState<Touched>({});
  const [passwordStatus, setPasswordStatus] = useState<PasswordStatus[]>([]);

  useEffect(() => {
    onValidate();
    // {reason}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account]);

  const onValidate = () => {
    const newErrors: Errors = {};

    if (account.email && !isValidEmail(account.email)) {
      newErrors.email = t("Auth.validation.emailInvalid");
    }

    if (initialData.email !== undefined && !account.email) {
      newErrors.email = t("Auth.validation.emailRequired");
    }

    if (initialData.password !== undefined && !account.password) {
      newErrors.password = t("Auth.validation.passwordRequired");
    }

    if (initialData.lastName !== undefined && !account.lastName) {
      newErrors.lastName = t("Auth.validation.lastNameRequired");
    }

    if (initialData.firstName !== undefined && !account.firstName) {
      newErrors.firstName = t("Auth.validation.firstNameRequired");
    }

    if (initialData.primaryLanguage !== undefined && !account.primaryLanguage) {
      newErrors.primaryLanguage = t("Auth.validation.primaryLanguageRequired");
    }

    if (initialData.location !== undefined && !account.location?.timezoneId) {
      newErrors.location = t("Auth.validation.locationRequired");
    }

    if (initialData.countryCode !== undefined && !account.countryCode?.code) {
      newErrors.countryCode = t("Auth.validation.countryRequired");
    }

    if (mfaMode && !account.code) {
      newErrors.code = t("Auth.validation.codeRequired");
    }

    if (initialData.confirmPassword !== undefined && account.password) {
      const lowercaseRegex = /[a-z]/;
      const uppercaseRegex = /[A-Z]/;
      const numberSymbolRegex = /[0-9\W]/;
      let status = [...passwordStatus];

      if (account.password.length < 8) {
        status = status.filter(item => item !== PasswordStatus.MinimumLength);
      } else {
        if (!status.includes(PasswordStatus.MinimumLength)) {
          status = [...status, PasswordStatus.MinimumLength];
        }
      }

      if (!lowercaseRegex.test(account.password)) {
        status = status.filter(item => item !== PasswordStatus.Lowercase);
      } else {
        if (!status.includes(PasswordStatus.Lowercase)) {
          status = [...status, PasswordStatus.Lowercase];
        }
      }

      if (!uppercaseRegex.test(account.password)) {
        status = status.filter(item => item !== PasswordStatus.Uppercase);
      } else {
        if (!status.includes(PasswordStatus.Uppercase)) {
          status = [...status, PasswordStatus.Uppercase];
        }
      }

      if (!numberSymbolRegex.test(account.password)) {
        status = status.filter(item => item !== PasswordStatus.NumberOrSymbol);
      } else {
        if (!status.includes(PasswordStatus.NumberOrSymbol)) {
          status = [...status, PasswordStatus.NumberOrSymbol];
        }
      }
      setPasswordStatus(status);
      if (status.length < 4) {
        newErrors.password = t("Auth.validation.passwordTooWeak");
      }
    }

    if (initialData.confirmPassword !== undefined && !account.confirmPassword) {
      newErrors.confirmPassword = t("Auth.validation.confirmPasswordRequired");
    }

    if (
      initialData.confirmPassword !== undefined &&
      account.confirmPassword !== account.password
    ) {
      newErrors.confirmPassword = t("Auth.validation.passwordMismatch");
    }

    setErrors(newErrors);
    return !Object.keys(newErrors).length;
  };

  const onTouched = (key: string) => {
    setTouched({ ...touched, [key]: true });
  };

  return {
    touched,
    account,
    setAccount,
    onValidate,
    onTouched,
    setTouched,
    errors,
    passwordStatus,
  };
}

export function isValidEmail(email: string) {
  return EMAIL_REGEX.test(email.trim());
}

export default useSignInForm;
